const formatDate = (value, lang = "ar") => (value ? new Date(value).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US", { timeZone: "UTC" }) : "-"); // billing days are UTC days: an end date of 23:59 UTC must not read as the next day in local time

/*
  Branch activation and coverage of ONE store, from the server (`overview.branchActivation`, calculated from the actual subscription and branch
  ids with the same rules the activation check uses). It keeps the four things apart:
    store completion (onboarding dashboard) / subscription status (cards above) / branch coverage (this table) / branch activation (this table).
  An uncovered branch points to the existing subscription flow; an expired subscription shows renewal without hiding anything else.
*/
const TEXT = {
  ar: {
    title: "تفعيل الفروع والتغطية",
    intro: "التغطية تُحسب لكل فرع على حدة من اشتراك المتجر. فرع بلا تغطية لا يمكن تفعيله، والفروع المسودة تبقى قابلة للتعديل.",
    branch: "الفرع",
    activation: "التفعيل",
    coverage: "التغطية",
    next: "الإجراء المطلوب",
    operational: "مفعّل",
    draft: "مسودة",
    other: "غير مفعّل",
    summary: "الفروع المغطّاة {covered} من {total} - غير المغطّاة {uncovered}",
    expiredNotice: "انتهت تغطية بعض فروعك المفعّلة. جدّد الاشتراك لتستمر تغطيتها؛ لا يتأثر بقية حسابك.",
    shadow: "وضع المراقبة: لا يُمنع أي إجراء حاليًا، لكن التغطية المطلوبة تُسجَّل.",
    states: {
      covered: "مغطّى",
      granted: "ممنوح من لود",
      converting: "قيد التحويل لمدفوع",
      pending_start: "يبدأ لاحقًا",
      awaiting_payment: "بانتظار السداد",
      renewal_overdue: "تجديد متأخر",
      expired: "منتهي",
      not_covered: "غير مغطّى",
    },
    until: "حتى",
    startsAt: "يبدأ",
    freeUntil: "التغطية المجانية حتى",
    actions: {
      cover_before_activation: "أضف تغطية قبل التفعيل",
      renew_or_cover: "جدّد أو أضف التغطية",
    },
    managedByLud: "اشتراك ممنوح/قيد التحويل: تُدار تغطيته من إدارة لود. تواصل مع الدعم لإضافة فروع.",
  },
  en: {
    title: "Branch activation and coverage",
    intro: "Coverage is calculated per branch from the store's subscription. A branch without coverage cannot be activated; draft branches stay editable.",
    branch: "Branch",
    activation: "Activation",
    coverage: "Coverage",
    next: "What to do",
    operational: "Operational",
    draft: "Draft",
    other: "Not operational",
    summary: "Covered branches {covered} of {total} - uncovered {uncovered}",
    expiredNotice: "Coverage ended for some of your operational branches. Renew the subscription to keep them covered; the rest of your account is unaffected.",
    shadow: "Monitoring mode: nothing is blocked right now, but missing coverage is recorded.",
    states: {
      covered: "Covered",
      granted: "Granted by LUD",
      converting: "Converting to paid",
      pending_start: "Starts later",
      awaiting_payment: "Awaiting payment",
      renewal_overdue: "Renewal overdue",
      expired: "Expired",
      not_covered: "Not covered",
    },
    until: "until",
    startsAt: "starts",
    freeUntil: "free coverage until",
    actions: {
      cover_before_activation: "Add coverage before activating",
      renew_or_cover: "Renew or add coverage",
    },
    managedByLud: "A granted / converting subscription is managed by LUD. Contact support to add branches.",
  },
};

const fill = (template, values) => Object.entries(values).reduce((out, [key, value]) => out.replace(`{${key}}`, String(value)), template);
const GOOD = new Set(["covered", "granted", "converting"]);

export default function BranchActivationSection({ activation, mode, lang, canAct, managedByLud, onCover }) {
  const t = TEXT[lang === "ar" ? "ar" : "en"];
  if (!activation?.branches?.length) return null;
  const { summary } = activation;
  const expiredOperational = activation.branches.some((branch) => branch.operational && ["expired", "renewal_overdue"].includes(branch.state));
  const detail = (branch) => {
    if (branch.state === "converting" && branch.freeUntil) return `${t.freeUntil} ${formatDate(branch.freeUntil, lang)}`;
    if (["covered", "granted"].includes(branch.state) && branch.until) return `${t.until} ${formatDate(branch.until, lang)}`;
    if (branch.state === "pending_start" && branch.startsAt) return `${t.startsAt} ${formatDate(branch.startsAt, lang)}`;
    return "";
  };
  return (
    <section className="cb-section" id="billingPanelBranchActivation" dir={lang === "ar" ? "rtl" : "ltr"}>
      <h2 className="cb-section-title">{t.title}</h2>
      <p className="cb-branch-intro">{t.intro}</p>
      <div className="cb-sub-meta" data-testid="branch-activation-summary">
        {fill(t.summary, { covered: summary.covered, total: summary.total, uncovered: summary.uncovered })}
      </div>
      {expiredOperational && <div className="cb-sub-note" role="alert">{t.expiredNotice}</div>}
      {mode === "shadow" && summary.operationalUncovered > 0 && <div className="cb-sub-note">{t.shadow}</div>}
      <div className="cb-table-scroll">
        <table className="cb-branch-track">
          <thead>
            <tr>
              <th>{t.branch}</th>
              <th>{t.activation}</th>
              <th>{t.coverage}</th>
              <th>{t.next}</th>
            </tr>
          </thead>
          <tbody>
            {activation.branches.map((branch) => (
              <tr key={branch.branchId} data-state={branch.state}>
                <td>{branch.name}</td>
                <td>{branch.operational ? t.operational : branch.draft ? t.draft : t.other}</td>
                <td>
                  <span className={`cb-coverage-state ${GOOD.has(branch.state) ? "good" : "bad"}`}>{t.states[branch.state] || branch.state}</span>
                  {detail(branch) && <small> {detail(branch)}</small>}
                </td>
                <td>
                  {branch.action && canAct && !managedByLud && (
                    <button type="button" className="cb-link-button" onClick={() => onCover(branch)}>
                      {t.actions[branch.action]}
                    </button>
                  )}
                  {branch.action && managedByLud && <small>{t.managedByLud}</small>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
